/**
 * useUnseen — DESIGN_CONTRACT §7 Awareness Contract.
 *
 * Hook for the "rose dot on rows new since I last looked" pattern.
 *
 * On mount it loads the user's seen-state for the given `kind` from the
 * backend (`/api/seen-state/{kind}`). Returns:
 *   {
 *     isUnseen(item)     — true if the item is new since last_seen_at
 *                          AND not in dismissed_ids,
 *     touch()            — bump last_seen_at = now (call once on mount),
 *     dismiss(id)        — mark id as seen / read,
 *     unseenCount        — convenience: count of unseen in the supplied items,
 *     loaded             — whether the seen-state has loaded yet,
 *   }
 *
 * Usage:
 *   const { isUnseen, touch, dismiss } = useUnseen("tickets", tickets);
 *   useEffect(() => { touch(); }, []);  // bump on first arrival
 *   <Row data-focus-id={t.id} className={isUnseen(t) ? "hv-unseen" : ""} … />
 *
 * Items are expected to expose either `created_at` or `updated_at`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "./api";

export default function useUnseen(kind, items = []) {
  const [state, setState] = useState({ last_seen_at: null, dismissed_ids: [] });
  const [loaded, setLoaded] = useState(false);

  // Load seen-state on mount / kind change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/seen-state/${kind}`);
        if (!cancelled) {
          setState({
            last_seen_at: data?.last_seen_at || null,
            dismissed_ids: data?.dismissed_ids || [],
          });
        }
      } catch (_e) {
        // Fail silent — unseen indicators are non-critical.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind]);

  const dismissedSet = useMemo(
    () => new Set(state.dismissed_ids || []),
    [state.dismissed_ids],
  );

  const isUnseen = useCallback(
    (item) => {
      if (!loaded || !item) return false;
      const id = item.id;
      if (!id) return false;
      if (dismissedSet.has(id)) return false;
      // No prior visit → everything is "unseen" — but that would flood the
      // UI on first open, so we treat first-visit as "all already seen".
      // The user sees the rose dot only on items created/updated AFTER
      // their first touch().
      if (!state.last_seen_at) return false;
      const stamp = item.updated_at || item.created_at;
      if (!stamp) return false;
      return stamp > state.last_seen_at;
    },
    [loaded, state.last_seen_at, dismissedSet],
  );

  const touch = useCallback(async () => {
    try {
      const { data } = await api.post(`/seen-state/${kind}/touch`);
      // Don't roll forward `last_seen_at` immediately — that would clear
      // the rose dots for items the user hasn't actually opened. Persist
      // server-side now; the *next* page load will use the new timestamp
      // as the reference. Old timestamp stays in local state for this
      // session so dots remain visible until the user navigates away.
      return data;
    } catch (_e) {
      // ignore
    }
  }, [kind]);

  const dismiss = useCallback(
    async (id) => {
      if (!id) return;
      // Optimistic update for snappy UX.
      setState((s) => ({
        ...s,
        dismissed_ids: s.dismissed_ids.includes(id)
          ? s.dismissed_ids
          : [...s.dismissed_ids, id],
      }));
      try {
        await api.post(`/seen-state/${kind}/dismiss`, { id });
      } catch (_e) {
        // ignore — optimistic state stays
      }
    },
    [kind],
  );

  const unseenCount = useMemo(() => {
    if (!loaded) return 0;
    return (items || []).filter((it) => isUnseen(it)).length;
  }, [loaded, items, isUnseen]);

  return { isUnseen, touch, dismiss, unseenCount, loaded };
}
